import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Tilt } from 'react-tilt';
import { useTheme } from '../context/ThemeContext';

/**
 * MonkeyTech — PortfolioSection (Ultra Premium)
 * - Brand mesh bg + grid
 * - Search (⌘K), category + tag filters
 * - Sort: Newest / A–Z / Category
 * - View: Grid (Tilt) / Masonry
 * - Shimmer image loader
 * - Bookmark (localStorage)
 * - Modal lightbox (image/video) with Esc + Arrow keys
 * - Share / Copy link
 * - Load More
 * - SEO: ItemList JSON-LD
 */

const RAW_ITEMS = [
  { title: 'Background Design', desc: 'Custom tech-themed background visuals.', img: '/images/data1.jpg', link: 'https://dawdul99.blogspot.com/2025/03/backraunt-imag.html', category: 'Design', tags: ['Visuals', 'Branding'], date: '2025-03-05' },
  { title: 'Virtual Assistant', desc: 'AI-powered sound-based virtual assistant.', img: '/images/data2.jpg', link: 'https://dawdul99.blogspot.com/2025/02/i-will-be-your-sound-virtual-assistant.html', category: 'AI', tags: ['Voice', 'Automation'], date: '2025-02-10' },
  { title: 'Data Entry & Web Research', desc: 'Reliable and accurate data management services.', img: '/images/data3.jpg', link: 'https://dawdul99.blogspot.com/2025/02/i-will-do-perfect-data-entry-web.html', category: 'Data', tags: ['Ops', 'ETL'], date: '2025-02-01' },
  { title: 'YouTube Thumbnail Design', desc: 'Creative thumbnail designs for your channel.', img: '/images/data4.jpg', link: 'https://dawdul99.blogspot.com/2025/02/thumbnail-design-for-biggest-youtube.html', category: 'Design', tags: ['Creator', 'Growth'], date: '2025-02-20' },
  // optional demo items (feel free to remove/replace)
  { title: 'Landing Revamp', desc: 'High-converting landing with A/B setup.', img: '/images/data1.jpg', link: 'https://example.com/landing', category: 'Web', tags: ['Next.js', 'SEO'], date: '2025-03-10' },
  { title: 'Insights Dashboard', desc: 'Power BI + automated reporting pipeline.', img: '/images/data3.jpg', link: 'https://example.com/bi', category: 'Data', tags: ['Power BI', 'KPI'], date: '2025-03-01' },
];

const sectionVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } },
};

// small helper
const isVideo = (src = '') => /\.(mp4|webm|ogg)$/i.test(src);

export default function PortfolioSection() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const TOKENS = isDark
    ? {
        bgStart: '#0B1220', bgMid: '#0B1325', bgEnd: '#000000',
        grid: 'rgba(255,255,255,0.25)',
        card: 'rgba(16,21,34,0.90)', cardInner: 'rgba(14,20,34,0.92)', cardBorder: 'rgba(255,255,255,0.10)',
        text: '#FFFFFF', muted: 'rgba(255,255,255,0.78)', shadow: '0 22px 46px rgba(0,0,0,0.35)',
      }
    : {
        bgStart: '#F6FAFF', bgMid: '#F0F4F9', bgEnd: '#FFFFFF',
        grid: 'rgba(0,0,0,0.08)',
        card: 'rgba(255,255,255,0.90)', cardInner: '#FFFFFF', cardBorder: 'rgba(0,0,0,0.08)',
        text: '#0B1220', muted: 'rgba(17,24,39,0.66)', shadow: '0 18px 34px rgba(2,6,23,0.10)',
      };

  // ---------- state
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('All');
  const [tag, setTag] = useState('All');
  const [sort, setSort] = useState('Newest');
  const [view, setView] = useState('grid'); // 'grid' | 'masonry'
  const [active, setActive] = useState(null); // active item
  const [loaded, setLoaded] = useState({});
  const [showCount, setShowCount] = useState(6);
  const [bookmarks, setBookmarks] = useState(() => {
    try { return JSON.parse(localStorage.getItem('mt-bookmarks')||'[]'); } catch { return []; }
  });
  const searchRef = useRef(null);

  // keyboard focus for ⌘K or '/'
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault(); searchRef.current?.focus();
      } else if (e.key === '/' && document.activeElement?.tagName !== 'INPUT') {
        e.preventDefault(); searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // modal keys
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setActive(null);
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active]);

  // share / copy
  const share = async (item) => {
    try {
      if (navigator.share) {
        await navigator.share({ title: item.title, text: item.desc, url: item.link });
      } else {
        await navigator.clipboard.writeText(item.link);
        alert('Link copied ✅');
      }
    } catch (_) {}
  };

  // bookmark
  const isBookmarked = (item) => bookmarks.includes(item.link);
  const toggleBookmark = (item) => {
    setBookmarks((prev) => {
      const next = prev.includes(item.link) ? prev.filter((l) => l !== item.link) : [...prev, item.link];
      localStorage.setItem('mt-bookmarks', JSON.stringify(next));
      return next;
    });
  };

  const items = RAW_ITEMS;

  const categories = useMemo(
    () => ['All', ...Array.from(new Set(items.map(i => i.category)))],
    [items]
  );

  const allTags = useMemo(
    () => ['All', ...Array.from(new Set(items.flatMap(i => i.tags || [])))],
    [items]
  );

  const filteredSorted = useMemo(() => {
    let base = items;
    if (filter !== 'All') base = base.filter(i => i.category === filter);
    if (tag !== 'All') base = base.filter(i => (i.tags || []).includes(tag));
    const q = query.trim().toLowerCase();
    if (q) base = base.filter(i =>
      i.title.toLowerCase().includes(q) ||
      i.desc.toLowerCase().includes(q) ||
      (i.tags || []).some(t => t.toLowerCase().includes(q))
    );
    // sort
    const sorters = {
      'Newest': (a,b) => new Date(b.date) - new Date(a.date),
      'A–Z': (a,b) => a.title.localeCompare(b.title),
      'Category': (a,b) => a.category.localeCompare(b.category) || a.title.localeCompare(b.title),
      'Bookmarked': (a,b) => (isBookmarked(b) - isBookmarked(a)) || new Date(b.date) - new Date(a.date),
    };
    base = [...base].sort(sorters[sort] || sorters['Newest']);
    return base;
  }, [items, filter, tag, query, sort, bookmarks]);

  // pagination
  const visible = filteredSorted.slice(0, showCount);

  // modal helpers
  const idxOfActive = active ? filteredSorted.findIndex(i => i.link === active.link) : -1;
  const go = (delta) => {
    if (idxOfActive < 0) return;
    const next = filteredSorted[(idxOfActive + delta + filteredSorted.length) % filteredSorted.length];
    setActive(next);
  };

  // JSON-LD
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'MonkeyTech Portfolio',
    itemListElement: filteredSorted.map((i, idx) => ({ '@type': 'ListItem', position: idx + 1, url: i.link, name: i.title, description: i.desc }))
  };

  return (
    <section
      className="relative py-20 px-4 sm:px-6 lg:px-8 font-inter overflow-hidden"
      aria-labelledby="portfolio-heading"
      style={{
        '--text': TOKENS.text,
        '--muted': TOKENS.muted,
        '--card': TOKENS.card,
        '--cardInner': TOKENS.cardInner,
        '--cardBorder': TOKENS.cardBorder,
        '--grid': TOKENS.grid,
      }}
    >
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${TOKENS.bgStart}, ${TOKENS.bgMid}, ${TOKENS.bgEnd})` }} />
        <div className="absolute -top-24 -left-24 h-[34rem] w-[34rem] rounded-full blur-3xl" style={{ background: 'radial-gradient(closest-side, rgba(31,165,80,0.20), transparent)' }} />
        <div className="absolute bottom-0 right-[-8rem] h-[30rem] w-[30rem] rounded-full blur-3xl" style={{ background: 'radial-gradient(closest-side, rgba(6,182,212,0.16), transparent)' }} />
        <div className="absolute inset-0 opacity-[0.06] [mask-image:linear-gradient(to_bottom,black,transparent_95%)]"
             style={{ backgroundImage: 'linear-gradient(var(--grid) 1px, transparent 1px), linear-gradient(90deg, var(--grid) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
      </div>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* Header */}
      <motion.div className="text-center mb-10 sm:mb-12 relative z-10" variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
        <h2 id="portfolio-heading" className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight" style={{ color: 'var(--text)' }}>
          Our <span className="bg-gradient-to-r from-[var(--brand,#1FA550)] to-[var(--accent,#06B6D4)] text-transparent bg-clip-text">Portfolio</span>
        </h2>
        <motion.p className="mt-5 text-lg sm:text-xl mx-auto max-w-3xl" style={{ color: 'var(--muted)' }} variants={sectionVariants} transition={{ delay: 0.15 }}>
          Explore our work across design, AI, web and data — built for performance and growth.
        </motion.p>
      </motion.div>

      {/* Controls */}
      <div className="relative z-10 max-w-7xl mx-auto mb-6 space-y-3">
        <div className="flex flex-col lg:flex-row items-center gap-3">
          {/* Search */}
          <label className="relative w-full lg:max-w-md">
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search… (press ⌘K or /)"
              className="w-full rounded-xl ring-1 focus:ring-2 px-4 py-3 outline-none backdrop-blur"
              style={{ background: 'var(--card)', color: 'var(--text)', borderColor: 'var(--cardBorder)' }}
              aria-label="Search portfolio"
            />
            <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted)' }}>⌘K</span>
          </label>

          {/* Category filter */}
          <nav className="flex flex-wrap justify-center gap-2 lg:ml-auto" aria-label="Category filter">
            {categories.map((cat) => {
              const active = filter === cat;
              return (
                <motion.button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className="px-4 py-2 rounded-full text-sm font-semibold ring-1 transition-all duration-300"
                  style={{ background: active ? 'var(--brand, #1FA550)' : 'var(--card)', color: active ? '#000' : 'var(--muted)', borderColor: 'var(--cardBorder)' }}
                  whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.96 }}
                  aria-current={active ? 'true' : 'false'}
                >
                  {cat}
                </motion.button>
              );
            })}
          </nav>
        </div>

        {/* Tag filter + sort + view */}
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            className="rounded-xl ring-1 px-3 py-2 text-sm"
            style={{ background: 'var(--card)', color: 'var(--text)', borderColor: 'var(--cardBorder)' }}
            aria-label="Tag filter"
          >
            {allTags.map((t) => <option key={t} value={t}>{t === 'All' ? 'All tags' : t}</option>)}
          </select>

          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="rounded-xl ring-1 px-3 py-2 text-sm"
            style={{ background: 'var(--card)', color: 'var(--text)', borderColor: 'var(--cardBorder)' }}
            aria-label="Sort"
          >
            <option>Newest</option>
            <option>A–Z</option>
            <option>Category</option>
            <option>Bookmarked</option>
          </select>

          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => setView('grid')}
              className={`px-3 py-2 rounded-xl text-sm ring-1 ${view==='grid' ? 'font-semibold' : ''}`}
              style={{ background: view==='grid' ? 'var(--brand,#1FA550)' : 'var(--card)', color: view==='grid' ? '#000' : 'var(--muted)', borderColor: 'var(--cardBorder)' }}
            >Grid</button>
            <button
              onClick={() => setView('masonry')}
              className={`px-3 py-2 rounded-xl text-sm ring-1 ${view==='masonry' ? 'font-semibold' : ''}`}
              style={{ background: view==='masonry' ? 'var(--brand,#1FA550)' : 'var(--card)', color: view==='masonry' ? '#000' : 'var(--muted)', borderColor: 'var(--cardBorder)' }}
            >Masonry</button>
          </div>
        </div>

        {/* Stats line */}
        <div className="text-sm" style={{ color: 'var(--muted)' }}>
          Showing <strong style={{ color: 'var(--text)' }}>{visible.length}</strong> of {filteredSorted.length} results • Bookmarked: {bookmarks.length}
        </div>
      </div>

      {/* GRID VIEW */}
      {view === 'grid' && (
        <div className="relative z-10 max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {visible.map((item, index) => (
            <Tilt key={item.link} options={{ max: 16, scale: 1.02, speed: 450, glare: true, 'max-glare': 0.15 }}>
              <Card
                item={item}
                index={index}
                loaded={!!loaded[index]}
                setLoaded={(ok) => setLoaded((m) => ({ ...m, [index]: ok }))}
                onPreview={() => setActive(item)}
                onVisit={() => window.open(item.link, '_blank', 'noopener')}
                onShare={() => share(item)}
                onBookmark={() => toggleBookmark(item)}
                bookmarked={isBookmarked(item)}
                TOKENS={TOKENS}
              />
            </Tilt>
          ))}
        </div>
      )}

      {/* MASONRY VIEW */}
      {view === 'masonry' && (
        <div className="relative z-10 max-w-7xl mx-auto columns-1 sm:columns-2 xl:columns-3 gap-6">
          {visible.map((item, index) => (
            <div key={item.link} className="mb-6 break-inside-avoid">
              <Card
                item={item}
                index={index}
                loaded={!!loaded[`m${index}`]}
                setLoaded={(ok) => setLoaded((m) => ({ ...m, [`m${index}`]: ok }))}
                onPreview={() => setActive(item)}
                onVisit={() => window.open(item.link, '_blank', 'noopener')}
                onShare={() => share(item)}
                onBookmark={() => toggleBookmark(item)}
                bookmarked={isBookmarked(item)}
                TOKENS={TOKENS}
              />
            </div>
          ))}
        </div>
      )}

      {/* Load more */}
      {showCount < filteredSorted.length && (
        <div className="relative z-10 mt-8 flex justify-center">
          <button
            onClick={() => setShowCount((c) => c + 6)}
            className="px-5 py-3 rounded-xl font-semibold text-black"
            style={{ background: 'linear-gradient(135deg, var(--brand,#1FA550), var(--accent,#06B6D4))' }}
          >
            Load more
          </button>
        </div>
      )}

      {/* Lightbox Modal */}
      <AnimatePresence>
        {active && (
          <motion.div className="fixed inset-0 z-50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/65" onClick={() => setActive(null)} />
            <motion.div
              className="relative mx-auto mt-10 sm:mt-16 max-w-5xl w-[92vw] rounded-2xl overflow-hidden ring-1 ring-white/10"
              style={{ background: 'var(--cardInner)' }}
              initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
              role="dialog" aria-modal="true" aria-label={`${active.title} preview`}
            >
              <div className="relative w-full bg-black">
                {isVideo(active.img)
                  ? <video src={active.img} className="w-full max-h-[70vh] object-contain" autoPlay controls muted />
                  : <img src={active.img} alt={active.title} className="w-full h-auto max-h-[70vh] object-contain" />
                }
                <button onClick={() => setActive(null)} className="absolute top-3 right-3 inline-flex items-center justify-center h-9 w-9 rounded-full bg-black/60 text-white hover:bg-black/80">✕</button>
                <button onClick={() => go(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 px-3 py-2 rounded-lg bg-black/60 text-white hover:bg-black/80">←</button>
                <button onClick={() => go(1)} className="absolute right-3 top-1/2 -translate-y-1/2 px-3 py-2 rounded-lg bg-black/60 text-white hover:bg-black/80">→</button>
              </div>
              <div className="p-4 sm:p-5">
                <h4 className="text-lg font-semibold" style={{ color: 'var(--text)' }}>{active.title}</h4>
                <p className="mt-1 text-sm" style={{ color: 'var(--muted)' }}>{active.desc}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <a href={active.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold text-black" style={{ background: 'linear-gradient(135deg, var(--brand,#1FA550), var(--accent,#06B6D4))' }}>Visit project</a>
                  <button onClick={() => share(active)} className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold bg-white/10 ring-1 ring-white/20 text-white hover:bg-white/15">Share</button>
                  <button onClick={() => setActive(null)} className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold bg-white/10 ring-1 ring-white/20 text-white hover:bg-white/15">Close</button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Styles */}
      <style>{`
        .font-inter{font-family:Inter,ui-sans-serif,system-ui,-apple-system,'Segoe UI',Roboto,Helvetica,Arial}
        .break-inside-avoid{break-inside:avoid}
        @keyframes shimmer{0%{background-position:-450px 0}100%{background-position:450px 0}}
      `}</style>
    </section>
  );
}

/* ===================== Card Component ===================== */
function Card({ item, index, loaded, setLoaded, onPreview, onVisit, onShare, onBookmark, bookmarked, TOKENS }) {
  return (
    <motion.article
      className="group relative rounded-2xl p-[1px]"
      initial={{ opacity: 0, y: 18, scale: 0.96 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      role="article"
      aria-labelledby={`portfolio-title-${index}`}
      tabIndex={0}
      style={{
        background:
          'conic-gradient(from 90deg at 50% 50%, rgba(31,165,80,.28), rgba(6,182,212,.28), transparent 65%)',
        boxShadow: TOKENS.shadow,
      }}
    >
      <div className="relative rounded-2xl h-full ring-1 overflow-hidden" style={{ background: 'var(--cardInner)', borderColor: 'var(--cardBorder)' }}>
        {/* top actions */}
        <div className="absolute top-3 right-3 z-10 flex gap-2">
          <button onClick={onShare} className="px-3 py-1.5 rounded-full text-xs font-semibold bg-white/10 ring-1 ring-white/20 text-white hover:bg-white/15">Share</button>
          <button onClick={onBookmark} className="px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: bookmarked ? 'var(--brand,#1FA550)' : 'rgba(255,255,255,0.10)', color: bookmarked ? '#000' : '#fff', border: '1px solid rgba(255,255,255,0.2)' }}>
            {bookmarked ? 'Bookmarked' : 'Bookmark'}
          </button>
        </div>

        {/* Image / video */}
        <div className="relative w-full aspect-[16/10] overflow-hidden">
          {!loaded && (
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(90deg, rgba(2,6,23,0.06) 25%, rgba(2,6,23,0.12) 37%, rgba(2,6,23,0.06) 63%)',
                backgroundSize: '450px 100%',
                animation: 'shimmer 1.2s linear infinite',
              }}
            />
          )}
          {isVideo(item.img) ? (
            <video
              src={item.img}
              className="absolute inset-0 w-full h-full object-cover"
              onLoadedData={() => setLoaded(true)}
              onError={() => setLoaded(true)}
              muted
            />
          ) : (
            <img
              src={item.img}
              alt={item.title}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
              onLoad={() => setLoaded(true)}
              onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/800x500?text=Preview'; setLoaded(true); }}
            />
          )}
        </div>

        {/* Body */}
        <div className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold"
                  style={{ background: 'rgba(31,165,80,0.12)', color: 'var(--text)', border: '1px solid var(--cardBorder)' }}>
              {item.category}
            </span>
            {(item.tags || []).map((t) => (
              <span key={t} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px]"
                    style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--muted)', border: '1px solid var(--cardBorder)' }}>
                {t}
              </span>
            ))}
          </div>

          <h3 id={`portfolio-title-${index}`} className="text-lg font-semibold" style={{ color: 'var(--text)' }}>
            {item.title}
          </h3>
          <p className="mt-1 text-sm" style={{ color: 'var(--muted)' }}>{item.desc}</p>
        </div>

        {/* Hover actions */}
        <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4 flex gap-2 pointer-events-auto">
            <button
              onClick={onVisit}
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold bg-[var(--brand,#1FA550)] text-black hover:opacity-95"
            >
              Visit
            </button>
            <button
              onClick={onPreview}
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold bg-white/10 ring-1 ring-white/20 text-white hover:bg-white/15"
            >
              Preview
            </button>
          </div>
        </div>
      </div>
    </motion.article>
  );
}
